import * as React from "react";
import { AxiosInstance } from "axios";
import { ServiceKind, updateConnectionMeta } from "./updateConnectionMeta";


type HookBase = {
    api: AxiosInstance;
    kind: ServiceKind;
    resourceId: string | null | undefined;
    initialName: string;
    initialTag: string;
    onPatched?: (p: { name?: string; tag?: string }) => void;
  };

type UseMetaEditorArgs = HookBase & {
    tenantId: number;
  };

  export function useMetaEditor(args: UseMetaEditorArgs) {
    const {
      api,
      kind,
      resourceId,
      initialName,
      initialTag,
      onPatched,
      tenantId,
    } = args;

  const [nameValue, setNameValue] = React.useState(initialName);
  const [tagValue,  setTagValue]  = React.useState(initialTag);

  const [originalName, setOriginalName] = React.useState(initialName);
  const [originalTag,  setOriginalTag]  = React.useState(initialTag);

  const [isEditingName, setIsEditingName] = React.useState(false);
  const [isEditingTag,  setIsEditingTag]  = React.useState(false);

  const [savingField, setSavingField] = React.useState<null | "name" | "tag">(null);
  const [isSavingTag, setIsSavingTag] = React.useState(false);
  
  React.useEffect(() => { setNameValue(initialName); setOriginalName(initialName); }, [initialName]);
  React.useEffect(() => { setTagValue(initialTag);   setOriginalTag(initialTag);   }, [initialTag]);

  const saveName = React.useCallback(async (nextName?: string) => {
    if (!resourceId) return;
    const effectiveName = nextName ?? nameValue;
    const effectiveTag  = tagValue;
    try {
      setSavingField("name");
      await updateConnectionMeta(api, {
        kind,
        resourceId,
        tenantId,
        data: { name: effectiveName, tag: effectiveTag },
      });
      setNameValue(effectiveName);
      setOriginalName(effectiveName);
      setIsEditingName(false);
      onPatched?.({ name: effectiveName });
    } finally {
      setSavingField(null);
    }
  }, [api, kind, resourceId, tenantId, nameValue, tagValue, onPatched]);

  const saveTag = React.useCallback(async (nextTag?: string) => {
    if (!resourceId) return;
    const effectiveTag  = nextTag ?? tagValue;
    const effectiveName = nameValue;
    try {
      setSavingField("tag");
      await updateConnectionMeta(api, {
        kind,
        resourceId,
        tenantId,
        data: { name: effectiveName, tag: effectiveTag },
      });
      setTagValue(effectiveTag);
      setOriginalTag(effectiveTag);
      setIsEditingTag(false);
      onPatched?.({ tag: effectiveTag });
    } finally {
      setSavingField(null);
    }
  }, [api, kind, resourceId, tenantId, nameValue, tagValue, onPatched]);

  const cancelName = React.useCallback(() => { setNameValue(originalName); setIsEditingName(false); }, [originalName]);
  const cancelTag  = React.useCallback(() => { setTagValue(originalTag);   setIsEditingTag(false);  }, [originalTag]);

  // Array-based tag operations
  const getTagArray = React.useCallback((): string[] => {
    if (!tagValue || (typeof tagValue === "string" && tagValue.trim() === "")) return [];
    // tagValue is always a string (comma-separated), split it
    return typeof tagValue === "string" 
      ? tagValue.split(",").map(t => t.trim()).filter(t => t !== "")
      : [];
  }, [tagValue]);

  const updateTagArray = React.useCallback(async (newTags: string[]) => {
    if (!resourceId) return;
    const tagString = newTags.join(", ");
    const effectiveName = nameValue;
    try {
      setIsSavingTag(true);
      setSavingField("tag");
      await updateConnectionMeta(api, {
        kind,
        resourceId,
        tenantId,
        data: { name: effectiveName, tag: tagString },
      });
      setTagValue(tagString);
      setOriginalTag(tagString);
      onPatched?.({ tag: tagString });
    } finally {
      setIsSavingTag(false);
      setSavingField(null);
    }
  }, [api, kind, resourceId, tenantId, nameValue, onPatched]);

  return {
    nameValue, tagValue, isEditingName, isEditingTag, savingField, isSavingTag,
    setNameValue, setTagValue, setIsEditingName, setIsEditingTag,
    saveName, saveTag, cancelName, cancelTag,
    // Array operations
    getTagArray, updateTagArray,
  }

  }